import { extractIdFromGid } from 'hooks/useStorefront'
import { formatMoney as shopifyFormatMoney } from '@shopify/theme-currency'
import { executeGraphQlQuery } from '../helpers/storefront-graphql-helper'

const settings = window.GM_STATE
const customBadgeItems = settings?.customs?.badge?.items || []
const { sizeUnit } = GM_STATE?.customs || {}

/**
 * Get shopify formatted price - applies a correction for Graphql responses
 * @param { string | number } inputCents
 * @param {*} format
 * @returns { string } formatted value
 */
const formatMoney = function (inputCents, format = settings.shopify.moneyFormat) {
  let outputCents = inputCents

  // the shopify function simply removes a period `.` from the value
  // graphql returns an input like 199.0 when it should return 199.00 - when passing into the shopify function, this returns a value of 19.90 instead of 199.00
  // this correction converts a single digit cents value into the correct length
  if (typeof inputCents === 'string' && inputCents.includes('.')) {
    let [dollars, cents] = inputCents.split('.')
    if (cents.length === 1) {
      cents = `${cents}0`
    }
    outputCents = `${dollars}${cents}`
  }

  return shopifyFormatMoney(outputCents, format)
}

const getProductTitle = (title) => {
  return title
}

const getProductSubtitle = (title) => {
  const textareaEl = document.createElement('textarea')
  textareaEl.innerHTML = title && title.includes('|') ? title.split(' | ')[1] : ''
  return textareaEl.value
}

const getCustomBadgeClass = (tagName) => {
  let tagClass = ''
  if (['new'].includes(tagName)) {
    tagClass = 'badge'
  }
  return tagClass
}

const getBadges = (tags) => {
  // Dynamically get the latest badge items instead of using the cached constant at module top
  const currentBadgeItems = window.GM_STATE?.customs?.badge?.items || []

  let items = []

  if (!tags.length) {
    return items
  }

  if (typeof tags === 'string' && tags.includes(',')) {
    try {
      tags = tags.split(',')
    } catch (err) {
      console.error('[getBadges] Could not parse tags:', tags, err)
      return items
    }
  }

  const labelTags = tags.filter(tag => tag.includes('label-'))

  items = labelTags
    .map(tag => {
      const foundItem = currentBadgeItems.find(item => item.tag === tag)
      return foundItem
    })
    .filter(x => x) // remove null

  return items
}

const getPriceSubscription = (planGroups, price) => {
  const planGroup = planGroups?.find(Boolean)
  const sellingPlans = planGroup?.selling_plans
  const priceAdjustments = sellingPlans?.find(Boolean).price_adjustments
  const priceAdjustment = priceAdjustments?.find(Boolean)
  let priceSubscription = price
  if (priceAdjustment?.value > 0) {
    if (priceAdjustment.value_type === 'percentage') {
      priceSubscription = priceSubscription - priceSubscription * priceAdjustment.value / 100
    } else {
      priceSubscription = priceSubscription - priceAdjustment.value
    }
  }
  return priceSubscription
}

const getSubscriptionSave = (planGroups) => {
  const planGroup = planGroups?.find(Boolean)
  const sellingPlans = planGroup?.selling_plans
  const priceAdjustments = sellingPlans?.find(Boolean).price_adjustments
  const priceAdjustment = priceAdjustments?.find(Boolean)
  let subscriptionSave = null
  if (priceAdjustment?.value > 0) {
    if (priceAdjustment.value_type === 'percentage') {
      subscriptionSave = `${priceAdjustment.value}%`
    } else {
      subscriptionSave = `${formatMoney(priceAdjustment.value)}`
    }
  }
  return subscriptionSave && `Subscribe & Save ${subscriptionSave}`
}

const formatAsPercentage = (number) => {
  return new Intl.NumberFormat('default', {
    style: 'percent',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  }).format(number / 100)
}

const addBadgeSale = (badges, price, originalPrice) => {
  if (price && originalPrice && price < originalPrice) {
    const salePercent = Math.round((originalPrice - price) / originalPrice * 100)
    const text = `${formatAsPercentage(salePercent)} off`
    badges.unshift({
      text,
      tag: 'label-sale',
      customClass: 'badge-card-sale',
      backgroundColor: '',
      textColor: ''
    })
  }

  return badges
}

const formatUnitSize = (size) => {
  return `$${size}`
}

// const formatRangePrice = (values) => {
//   console.log(values, 'values')
//   const priceValue = values?.map(([low, high]) => {
//     const lowPrice = low === '*' ? 0 : `$${low}`
//     const highPrice = high === '*' ? '+' : `$${high}`
//     console.log(lowPrice, highPrice, 'lowPricehighPrice')
//     return highPrice !== '+' ? `${lowPrice} - ${highPrice}` : `${lowPrice}${highPrice}`
//   }).join(' | ') || ''
//   console.log(priceValue, 'priceValue')
//   return priceValue
// }
const formatRangePrice = (values) => {
  if (!values || values.length === 0) return ''

  const ranges = Array.isArray(values[0]) ? values : [values]
  const rangesValue = ranges
    .map(([low, high]) => {
      const lowPrice = low === '*' ? 0 : `$${low}`
      const highPrice = high === '*' ? '+' : `$${high}`
      return highPrice !== '+'
        ? `${lowPrice} - ${highPrice}`
        : `${lowPrice}${highPrice}`
    })
    .join(' | ')
  // console.log(rangesValue, 'rangesValue')
  return rangesValue
}

const formatRangeSize = (values) => values && values.map(item => formatUnitSize(item)).join(' - ')

const initInstance = (callback, ctx = this) => {
  const isDesignMode = window.Shopify.designMode || false

  return (...args) => {
    callback.apply(ctx, args) // Init instance
    isDesignMode && document.addEventListener('shopify:section:load', () => callback.apply(ctx, args))
  }
}

const getDefaultImage = (img) => {
  return (Array.isArray(img) && img.length === 0) || !img ? GM_STATE.shopify.defaultImage : img
}

const handlePromoDiscounts = (cartItems) => {
  let codes = []

  cartItems.forEach(cartItem => {
    const appliedDiscountCodes = cartItem.discounts.map(discount => discount.title)
    const validDiscounts = settings?.metaobjects.promo_discounts?.filter(promoDiscount => {
      if (promoDiscount.enable && !appliedDiscountCodes.includes(promoDiscount.discount_code) && promoDiscount.products && promoDiscount.products.length) {
        const productIndex = promoDiscount.products.findIndex(productGid => {
          return cartItem.product_id === extractIdFromGid(productGid)
        })
        return productIndex >= 0
      }
      return false
    })
    const validPromoDiscounts = validDiscounts?.map(validDiscount => ({
      title: validDiscount.discount_code,
      amount: validDiscount.is_fixed_amount ? Number(validDiscount.fixed_amount_value) * 100 : cartItem.original_price * Number(validDiscount.percentage) / 100
    }))

    if (validPromoDiscounts) {
      cartItem.discounts = [...cartItem.discounts, ...validPromoDiscounts]
    }

    if (validDiscounts && validDiscounts.length) {
      codes = [...codes, ...validDiscounts.map(discount => discount.discount_code)]
    }
  })

  if (codes.length) {
    codes = codes.filter((code, index, arr) => arr.indexOf(code) === index)
  }

  return codes
}

const getQueryCustomerAccessToken = () => {
  return `mutation customerAccessTokenCreate($input: CustomerAccessTokenCreateInput!) {
    customerAccessTokenCreate(input: $input) {
      customerAccessToken {
        accessToken
        expiresAt
      }
      customerUserErrors {
        code
        field
        message
      }
    }
  }`
}

const getCustomerAccessToken = async ({ email, password }) => {
  const variables = {
    input: {
      email,
      password
    }
  }
  const query = getQueryCustomerAccessToken()

  const res = await executeGraphQlQuery({ query, variables })
  const data = await res.json()
  return data
}

const createCustomer = async ({ email, password, firstName, lastName }) => {
  const variables = {
    input: {
      email,
      password,
      firstName,
      lastName
    }
  }
  const query = getQueryCustomerCreate()

  const res = await executeGraphQlQuery({ query, variables })
  const data = await res.json()
  return data
}

const getQueryCustomerCreate = () => {
  return `mutation customerCreate($input: CustomerCreateInput!) {
    customerCreate(input: $input) {
      customer {
        email
        firstName
        lastName
      }
      customerUserErrors {
        field
        message
        code
      }
    }
  }`
}

const getQueryCustomerUpdate = () => {
  return `mutation customerUpdate($customerAccessToken: String!, $customer: CustomerUpdateInput!) {
    customerUpdate(customerAccessToken: $customerAccessToken, customer: $customer) {
      customer {
        email
        firstName
        lastName
        phone
      }
      customerAccessToken {
        accessToken
        expiresAt
      }
      userErrors {
        field
        message
      }
    }
  }`
}

export {
  formatMoney,
  getProductTitle,
  getProductSubtitle,
  getBadges,
  getCustomBadgeClass,
  getPriceSubscription,
  getSubscriptionSave,
  addBadgeSale,
  formatRangePrice,
  initInstance,
  getDefaultImage,
  formatRangeSize,
  formatUnitSize,
  handlePromoDiscounts,
  getCustomerAccessToken,
  createCustomer,
  getQueryCustomerUpdate
}
